import { useSearchParams } from "react-router-dom";

// Where to go after sign-in / sign-up. Clerk's OAuth round trip (Google) comes back to the auth
// page without our query string, so the destination is also kept for the tab's session. Stored
// with a timestamp so an abandoned sign-in doesn't hijack a later, unrelated one.
const STORAGE_KEY = "auth-redirect";
const MAX_AGE_MS = 30 * 60 * 1000;
const DEFAULT_REDIRECT = "/dashboard";

/** Only same-site paths: "/demo/…", never "//evil.com" or "https://…". */
export const safeRedirect = (value: string | null | undefined): string | null =>
  value?.startsWith("/") && !value.startsWith("//") ? value : null;

const readStored = (): string | null => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? "null") as {
      path?: string;
      at?: number;
    } | null;
    if (!stored?.path || !stored.at || Date.now() - stored.at > MAX_AGE_MS) return null;
    return safeRedirect(stored.path);
  } catch {
    return null;
  }
};

/** The page to land on after auth: ?redirect=, else the remembered one, else the dashboard. */
export const useAuthRedirect = (): string => {
  const [searchParams] = useSearchParams();
  const fromQuery = safeRedirect(searchParams.get("redirect"));
  try {
    if (fromQuery)
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ path: fromQuery, at: Date.now() }));
  } catch {
    // Storage blocked: the query string still works for email sign-in.
  }
  return fromQuery ?? readStored() ?? DEFAULT_REDIRECT;
};

/** Called once the visitor is on their way, so the next sign-in starts fresh. */
export const clearAuthRedirect = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * The link between sign-in and sign-up inside Clerk's forms. Keeps ?redirect= and ?brand= so an
 * existing user who lands on sign-up (or a new one on sign-in) still ends up where they were going.
 */
export const withAuthQuery = (path: string, search: string) => `${path}${search}`;
